import React from 'react';
import { Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useAccommodationMapListSyncApi } from '@apps/stays/AccommodationsConsole/components/AccommodationMapListSync/AccommodationMapListSync';
import { useTranslation } from '@shared/core';
import { useEventLocaleFormatter } from '@shared/core/localization/hooks/useEventLocaleFormatter';

// Shared layout container — highlights listing on map hover.
export const ListingContainer = ({
  children,
  id,
  joyPlaceId,
  borderColor = 'mono3',
  backgroundColor,
}: {
  children: React.ReactNode;
  id?: string;
  joyPlaceId: Maybe<string>;
  /** Shell border on tablet and up; the cash-fund variant passes its own. */
  borderColor?: string;
  /** Shell fill; undefined leaves it transparent. */
  backgroundColor?: string;
}) => {
  const { onHoverStart, onHoverEnd } = useAccommodationMapListSyncApi();

  const handleMouseEnter = useEventCallback(() => {
    if (joyPlaceId) {
      onHoverStart(joyPlaceId);
    }
  });

  const handleMouseLeave = useEventCallback(() => {
    if (joyPlaceId) {
      onHoverEnd();
    }
  });

  return (
    <Flex
      id={id}
      flexDirection={{ _: 'column', sm2: 'row' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      borderRadius={{ _: 0, sm2: pxToRem(10) }}
      border={{ _: 'none', sm2: '1px solid' }}
      borderColor={{ _: 'transparent', sm2: borderColor }}
      backgroundColor={backgroundColor}
      overflow={{ _: 'visible', sm2: 'hidden' }}
    >
      {children}
    </Flex>
  );
};

export const ContentPanel = ({ children }: { children: React.ReactNode }) => {
  return (
    <Flex
      flexDirection="column"
      width={'100%'}
      gap={{ _: 4, sm2: 0 }}
      paddingY={{ _: 4, sm2: pxToRem(12) }}
      paddingLeft={{ _: 0, sm2: pxToRem(16) }}
      paddingRight={{ _: 0, sm2: pxToRem(16) }}
    >
      {children}
    </Flex>
  );
};

export const LogisticsAndCostBlock = ({ children }: { children: React.ReactNode }) => {
  return (
    <Flex
      flexDirection={{ _: 'column', sm2: 'row' }}
      gap={{ _: 4, sm2: 0 }}
      marginTop={{ _: 0, sm2: 6 }}
      justifyContent={{ _: 'flex-start', sm2: 'space-between' }}
      alignItems={{ _: 'none', sm2: 'center' }}
      width={{ _: '100%', sm2: 'auto' }}
    >
      {children}
    </Flex>
  );
};

export const CutoffDate = ({ cutoffDate }: { cutoffDate: string }) => {
  const { tq } = useTranslation('accommodationsConsole');
  const { formatDate } = useEventLocaleFormatter();
  const formattedCutoffDate = formatDate(new Date(cutoffDate), 'MMMM do, yyyy') ?? '';

  return (
    <Flex justifyContent={'flex-end'}>
      <TextV2
        typographyVariant="label1"
        color={'mono10'}
      >
        {tq`Book before ${formattedCutoffDate}`}
      </TextV2>
    </Flex>
  );
};
